import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  User, Pill, Activity, Calendar, FileText, AlertTriangle,
  ClipboardList, LogOut, Heart, Clock, MapPin, Stethoscope, Lock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/famiglia`;

function FamigliaLogin({ onLogin }) {
  const [codice, setCodice] = useState('');
  const [cognome, setCognome] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/login`, { codice, cognome });
      onLogin(res.data.token, res.data.ospite_nome, res.data.referente);
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore di accesso');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center px-4">
      <Card className="w-full max-w-md shadow-xl border-0">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-blue-100 flex items-center justify-center">
            <Heart className="w-8 h-8 text-brand" />
          </div>
          <CardTitle className="text-xl font-bold text-slate-800">Portale Famiglie</CardTitle>
          <p className="text-sm text-slate-500">Santa Maria Valleverde</p>
          <p className="text-xs text-slate-400 mt-1">Inserisci il codice di accesso fornito dalla struttura</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-xs">Codice di Accesso</Label>
              <Input
                value={codice}
                onChange={e => setCodice(e.target.value.toUpperCase())}
                placeholder="Es. A1B2C3D4"
                className="font-mono tracking-widest text-center text-lg"
                maxLength={8}
                data-testid="famiglia-codice"
              />
            </div>
            <div>
              <Label className="text-xs">Cognome dell'Ospite</Label>
              <Input
                value={cognome}
                onChange={e => setCognome(e.target.value)}
                placeholder="Cognome del vostro familiare"
                data-testid="famiglia-cognome"
              />
            </div>
            {error && <p className="text-xs text-red-500 text-center">{error}</p>}
            <Button type="submit" disabled={loading || !codice || !cognome} className="w-full bg-brand hover:bg-brand-dark" data-testid="famiglia-login-btn">
              <Lock className="w-4 h-4 mr-2" />{loading ? 'Accesso...' : 'Accedi'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function FamigliaDashboard({ token, ospiteNome, referente, onLogout }) {
  const [profilo, setProfilo] = useState(null);
  const [terapie, setTerapie] = useState([]);
  const [parametri, setParametri] = useState([]);
  const [appuntamenti, setAppuntamenti] = useState([]);
  const [diario, setDiario] = useState([]);
  const [eventi, setEventi] = useState([]);
  const [pai, setPai] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    const headers = { Authorization: `Bearer ${token}` };
    setLoading(true);
    try {
      const [prof, ter, par, app, dia, ev, pa] = await Promise.all([
        axios.get(`${API}/profilo`, { headers }),
        axios.get(`${API}/terapie`, { headers }),
        axios.get(`${API}/parametri`, { headers }),
        axios.get(`${API}/appuntamenti`, { headers }),
        axios.get(`${API}/diario`, { headers }),
        axios.get(`${API}/eventi`, { headers }),
        axios.get(`${API}/pai`, { headers }),
      ]);
      setProfilo(prof.data);
      setTerapie(ter.data);
      setParametri(par.data);
      setAppuntamenti(app.data);
      setDiario(dia.data);
      setEventi(ev.data);
      setPai(pa.data);
    } catch (e) {
      console.error('Sessione familiare non valida:', e);
      onLogout();
    }
    setLoading(false);
  }, [token, onLogout]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const formatDate = (d) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleDateString('it-IT'); } catch { return d; }
  };
  const formatDateTime = (d) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return d; }
  };

  // Group vitals by latest date
  const latestVitals = {};
  parametri.forEach(p => {
    if (!latestVitals[p.tipo] || p.data_rilevazione > latestVitals[p.tipo].data_rilevazione) {
      latestVitals[p.tipo] = p;
    }
  });

  const vitalLabels = { PA_sistolica: 'PA Sist.', PA_diastolica: 'PA Diast.', FC: 'Freq. Card.', SpO2: 'Saturazione', temperatura: 'Temperatura', glicemia: 'Glicemia' };

  if (loading) return (
    <div className="min-h-screen bg-blue-50 flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-slate-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center">
              <Heart className="w-5 h-5 text-brand" />
            </div>
            <div>
              <h1 className="text-sm font-bold text-slate-800">{ospiteNome}</h1>
              <p className="text-[10px] text-slate-400">Benvenuto/a, {referente}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onLogout} className="text-slate-400 hover:text-red-500" data-testid="famiglia-logout">
            <LogOut className="w-4 h-4 mr-1" />Esci
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Profile card */}
        {profilo && (
          <Card className="border-0 shadow-md" data-testid="famiglia-profilo">
            <CardContent className="p-5">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <User className="w-7 h-7 text-brand" />
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-bold text-slate-800">{profilo.cognome} {profilo.nome}</h2>
                  <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(profilo.data_nascita)}</span>
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />Camera {profilo.stanza || '-'} / Letto {profilo.letto || '-'}</span>
                    <span className="flex items-center gap-1"><Stethoscope className="w-3 h-3" />{profilo.medico_base || '-'}</span>
                  </div>
                </div>
                <Badge className={profilo.stato === 'presente' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}>{profilo.stato}</Badge>
              </div>

              {/* Quick vitals */}
              {Object.keys(latestVitals).length > 0 && (
                <div className="mt-4 grid grid-cols-3 sm:grid-cols-6 gap-2">
                  {Object.entries(latestVitals).map(([tipo, v]) => (
                    <div key={tipo} className={`text-center p-2 rounded-lg ${v.fuori_range ? 'bg-red-50' : 'bg-slate-50'}`}>
                      <p className="text-[9px] text-slate-400 uppercase">{vitalLabels[tipo] || tipo}</p>
                      <p className={`text-sm font-bold ${v.fuori_range ? 'text-red-600' : 'text-slate-700'}`}>{v.valore}</p>
                      <p className="text-[8px] text-slate-300">{v.unita}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs defaultValue="terapie" className="w-full">
          <TabsList className="grid grid-cols-3 sm:grid-cols-6 w-full h-auto bg-white border shadow-sm rounded-xl p-1">
            <TabsTrigger value="terapie" className="text-xs data-[state=active]:bg-brand-soft data-[state=active]:text-brand-dark rounded-lg py-2" data-testid="famiglia-tab-terapie">
              <Pill className="w-3.5 h-3.5 mr-1" />Terapie
            </TabsTrigger>
            <TabsTrigger value="appuntamenti" className="text-xs data-[state=active]:bg-brand-soft data-[state=active]:text-brand-dark rounded-lg py-2" data-testid="famiglia-tab-appuntamenti">
              <Calendar className="w-3.5 h-3.5 mr-1" />Visite
            </TabsTrigger>
            <TabsTrigger value="diario" className="text-xs data-[state=active]:bg-brand-soft data-[state=active]:text-brand-dark rounded-lg py-2" data-testid="famiglia-tab-diario">
              <FileText className="w-3.5 h-3.5 mr-1" />Diario
            </TabsTrigger>
            <TabsTrigger value="parametri" className="text-xs data-[state=active]:bg-brand-soft data-[state=active]:text-brand-dark rounded-lg py-2" data-testid="famiglia-tab-parametri">
              <Activity className="w-3.5 h-3.5 mr-1" />Parametri
            </TabsTrigger>
            <TabsTrigger value="pai" className="text-xs data-[state=active]:bg-brand-soft data-[state=active]:text-brand-dark rounded-lg py-2" data-testid="famiglia-tab-pai">
              <ClipboardList className="w-3.5 h-3.5 mr-1" />PAI
            </TabsTrigger>
            <TabsTrigger value="eventi" className="text-xs data-[state=active]:bg-brand-soft data-[state=active]:text-brand-dark rounded-lg py-2" data-testid="famiglia-tab-eventi">
              <AlertTriangle className="w-3.5 h-3.5 mr-1" />Eventi
            </TabsTrigger>
          </TabsList>

          {/* TERAPIE */}
          <TabsContent value="terapie">
            <Card className="border-0 shadow-md">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Terapie Attive</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {terapie.length === 0 ? <p className="text-xs text-slate-400 text-center py-4">Nessuna terapia attiva</p> : terapie.map(t => (
                  <div key={t.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <Pill className="w-4 h-4 text-blue-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800">{t.farmaco}</p>
                      <p className="text-xs text-slate-400">{t.dosaggio} - {t.via} {t.orari?.length > 0 && `(${t.orari.join(', ')})`}</p>
                      {t.note && <p className="text-[10px] text-slate-400 mt-0.5">{t.note}</p>}
                    </div>
                    <span className="text-[10px] text-slate-300">dal {formatDate(t.durata_inizio)}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* APPUNTAMENTI */}
          <TabsContent value="appuntamenti">
            <Card className="border-0 shadow-md">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Visite e Appuntamenti</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {appuntamenti.length === 0 ? <p className="text-xs text-slate-400 text-center py-4">Nessun appuntamento</p> : appuntamenti.map(a => (
                  <div key={a.id} className={`p-3 rounded-lg border ${a.stato === 'programmato' ? 'border-blue-200 bg-blue-50/30' : 'border-slate-200 bg-slate-50'}`}>
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-slate-800">{a.tipo}</p>
                      <Badge className={`text-[9px] ${a.stato === 'programmato' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>{a.stato}</Badge>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatDateTime(a.data)}</span>
                      <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{a.luogo}</span>
                      <span>{a.medico}</span>
                    </div>
                    {a.note && <p className="text-[10px] text-slate-400 mt-1">{a.note}</p>}
                    {a.esito && <p className="text-xs text-emerald-600 mt-1 font-medium">Esito: {a.esito}</p>}
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* DIARIO */}
          <TabsContent value="diario">
            <Card className="border-0 shadow-md">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Diario Clinico</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {diario.length === 0 ? <p className="text-xs text-slate-400 text-center py-4">Nessuna annotazione</p> : diario.map((d, i) => (
                  <div key={i} className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className="text-[9px] bg-slate-200 text-slate-600">{d.turno}</Badge>
                      <span className="text-[10px] text-slate-400">{formatDate(d.data)}</span>
                      <span className="text-[10px] text-slate-400 ml-auto">{d.operatore_nome}</span>
                    </div>
                    <p className="text-xs text-slate-700">{d.contenuto}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* PARAMETRI */}
          <TabsContent value="parametri">
            <Card className="border-0 shadow-md">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Parametri Vitali (ultimi)</CardTitle></CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 text-slate-400 font-medium">Data</th>
                        <th className="text-left py-2 text-slate-400 font-medium">Tipo</th>
                        <th className="text-right py-2 text-slate-400 font-medium">Valore</th>
                        <th className="text-right py-2 text-slate-400 font-medium">Unita</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parametri.slice(0, 50).map((p, i) => (
                        <tr key={i} className={`border-b last:border-0 ${p.fuori_range ? 'bg-red-50' : ''}`}>
                          <td className="py-1.5 text-slate-500">{formatDateTime(p.data_rilevazione)}</td>
                          <td className="py-1.5 font-medium text-slate-700">{vitalLabels[p.tipo] || p.tipo}</td>
                          <td className={`py-1.5 text-right font-bold ${p.fuori_range ? 'text-red-600' : 'text-slate-700'}`}>{p.valore}</td>
                          <td className="py-1.5 text-right text-slate-400">{p.unita}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* PAI */}
          <TabsContent value="pai">
            <Card className="border-0 shadow-md">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Piano Assistenziale Individuale</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {pai.length === 0 ? <p className="text-xs text-slate-400 text-center py-4">Nessun PAI attivo</p> : pai.map((p, i) => (
                  <div key={i} className="space-y-3">
                    {p.valutazioni?.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-slate-600 mb-1">Valutazioni</h4>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {p.valutazioni.map((v, j) => (
                            <div key={j} className="p-2 bg-slate-50 rounded-lg text-center">
                              <p className="text-[9px] text-slate-400">{v.scala}</p>
                              <p className="text-sm font-bold text-slate-700">{v.punteggio}</p>
                              {v.note && <p className="text-[8px] text-slate-400">{v.note}</p>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {p.obiettivi?.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-slate-600 mb-1">Obiettivi</h4>
                        {p.obiettivi.map((o, j) => (
                          <div key={j} className="flex items-center gap-2 py-1">
                            <div className={`w-2 h-2 rounded-full ${o.stato === 'attivo' ? 'bg-blue-500' : 'bg-emerald-500'}`} />
                            <span className="text-xs text-slate-700">{o.descrizione}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {p.data_prossima_rivalutazione && (
                      <p className="text-[10px] text-slate-400">Prossima rivalutazione: {formatDate(p.data_prossima_rivalutazione)}</p>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* EVENTI */}
          <TabsContent value="eventi">
            <Card className="border-0 shadow-md">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Eventi Avversi</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {eventi.length === 0 ? <p className="text-xs text-slate-400 text-center py-4">Nessun evento avverso</p> : eventi.map((e, i) => (
                  <div key={i} className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className="text-[9px] bg-amber-100 text-amber-700">{e.tipo}</Badge>
                      <span className="text-[10px] text-slate-400">{formatDateTime(e.data_ora)}</span>
                    </div>
                    <p className="text-xs text-slate-700">{e.dinamica}</p>
                    {e.conseguenze && <p className="text-xs text-amber-700 mt-1">Conseguenze: {e.conseguenze}</p>}
                    {e.interventi && <p className="text-xs text-emerald-700 mt-1">Interventi: {e.interventi}</p>}
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      <footer className="text-center py-6 text-[10px] text-slate-300">
        Santa Maria Valleverde S.r.l. - Portale Famiglie
      </footer>
    </div>
  );
}

export default function PortaleFamiglie() {
  const [token, setToken] = useState(localStorage.getItem('famiglia_token'));
  const [ospiteNome, setOspiteNome] = useState(localStorage.getItem('famiglia_ospite') || '');
  const [referente, setReferente] = useState(localStorage.getItem('famiglia_ref') || '');

  const handleLogin = (t, nome, ref) => {
    setToken(t);
    setOspiteNome(nome);
    setReferente(ref);
    localStorage.setItem('famiglia_token', t);
    localStorage.setItem('famiglia_ospite', nome);
    localStorage.setItem('famiglia_ref', ref);
  };

  const handleLogout = useCallback(() => {
    setToken(null);
    setOspiteNome('');
    setReferente('');
    localStorage.removeItem('famiglia_token');
    localStorage.removeItem('famiglia_ospite');
    localStorage.removeItem('famiglia_ref');
  }, []);

  if (!token) return <FamigliaLogin onLogin={handleLogin} />;
  return <FamigliaDashboard token={token} ospiteNome={ospiteNome} referente={referente} onLogout={handleLogout} />;
}
